import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { TrendUp, ArrowsClockwise, Target, Lightning, Info } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Market Intelligence Dashboard card (Phase G.5 — Round 210).
 *
 * Reads GET /api/market-intelligence/dashboard-summary and lets the
 * owner force a refresh via POST /api/market-intelligence/refresh.
 * When no document exists yet, offers a one-click 'Generate' CTA.
 *
 * Deliberately compact: fits into the existing Dashboard grid without
 * adding a new page or route. Uses the same colour system as the
 * rest of the dashboard (slate + branded gradients).
 */
const MarketIntelligenceCard = () => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [showReasons, setShowReasons] = useState(false);

  const authHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const loadSummary = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API_URL}/api/market-intelligence/dashboard-summary`, {
        headers: authHeaders(),
      });
      setSummary(res.data);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || 'Failed to load Market Intelligence');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const handleRefresh = async () => {
    if (refreshing) return;
    setRefreshing(true);
    setError(null);
    try {
      await axios.post(`${API_URL}/api/market-intelligence/refresh`, {}, {
        headers: authHeaders(),
      });
      await loadSummary();
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || 'Refresh failed');
    } finally {
      setRefreshing(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
    } catch (_e) {
      return iso;
    }
  };

  if (loading) {
    return (
      <div
        className="mb-6 p-5 bg-gradient-to-br from-emerald-500/10 to-cyan-500/10 border border-emerald-500/30 rounded-xl"
        data-testid="market-intelligence-card-loading"
      >
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 border-2 border-emerald-400/30 border-t-emerald-400 rounded-full animate-spin" />
          <span className="text-emerald-300 text-sm">Loading Market Intelligence…</span>
        </div>
      </div>
    );
  }

  // First-run empty state
  if (!summary?.exists) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-6 p-5 bg-gradient-to-br from-emerald-500/10 to-cyan-500/10 border border-emerald-500/30 rounded-xl"
        data-testid="market-intelligence-card-empty"
      >
        <div className="flex items-center gap-2 mb-2">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-emerald-500 to-cyan-600 flex items-center justify-center">
            <TrendUp size={18} weight="fill" className="text-white" />
          </div>
          <div>
            <h3 className="text-white font-semibold text-base">Market Intelligence</h3>
            <p className="text-emerald-200/70 text-xs">Your ongoing Business Strategist</p>
          </div>
        </div>
        <p className="text-slate-300 text-sm mb-3 leading-relaxed">
          Generate a strategic snapshot from your Business Brain, Brand Brain and today&apos;s market
          catalog. Every builder will use it for smarter recommendations.
        </p>
        <button
          type="button"
          onClick={handleRefresh}
          disabled={refreshing}
          data-testid="mi-generate-btn"
          className="w-full py-2.5 bg-gradient-to-r from-emerald-500 to-cyan-600 hover:from-emerald-400 hover:to-cyan-500 text-white text-sm font-semibold rounded-lg flex items-center justify-center gap-2 transition-all disabled:opacity-60"
        >
          {refreshing ? (
            <>
              <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
              <span>Generating…</span>
            </>
          ) : (
            <>
              <Lightning size={16} weight="fill" />
              <span>Generate Market Intelligence</span>
            </>
          )}
        </button>
        {error && (
          <div className="mt-3 text-xs text-rose-300" data-testid="mi-error">
            {error}
          </div>
        )}
      </motion.div>
    );
  }

  const daysUntil = summary.days_until_refresh;
  const overdue = daysUntil !== null && daysUntil <= 0;
  const dueSoon = daysUntil !== null && daysUntil > 0 && daysUntil <= 7;
  const confidence = summary.confidence || 0;
  const confidenceTone =
    confidence >= 80 ? 'text-emerald-300' :
    confidence >= 60 ? 'text-cyan-300' :
    confidence >= 40 ? 'text-amber-300' :
    'text-rose-300';

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="mb-6 p-5 bg-gradient-to-br from-emerald-500/10 to-cyan-500/10 border border-emerald-500/30 rounded-xl"
      data-testid="market-intelligence-card"
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-emerald-500 to-cyan-600 flex items-center justify-center">
            <TrendUp size={18} weight="fill" className="text-white" />
          </div>
          <div>
            <h3 className="text-white font-semibold text-base leading-tight">Market Intelligence</h3>
            <p className="text-emerald-200/70 text-xs">
              v{summary.version} · Updated {formatDate(summary.last_updated)}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={handleRefresh}
          disabled={refreshing}
          data-testid="mi-refresh-btn"
          className="px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white text-xs font-medium rounded-lg flex items-center gap-1.5 transition-all disabled:opacity-60"
        >
          <ArrowsClockwise size={13} weight="bold" className={refreshing ? 'animate-spin' : ''} />
          <span>{refreshing ? 'Refreshing…' : 'Refresh'}</span>
        </button>
      </div>

      {/* Refresh due status */}
      <div className="mb-3 flex items-center gap-2 text-xs">
        <span className="text-slate-400">Next refresh:</span>
        <span className={overdue ? 'text-rose-300 font-semibold' : dueSoon ? 'text-amber-300 font-semibold' : 'text-slate-300'}>
          {overdue ? 'Overdue — refresh recommended' :
            daysUntil === null ? formatDate(summary.refresh_due_at) :
            `in ${daysUntil} day${daysUntil === 1 ? '' : 's'}`}
        </span>
      </div>

      {/* Monthly strategy nudge — only when the document is overdue.
          Deliberately subtle: one line, no dismiss button, no toast. */}
      {overdue && (
        <div
          className="mb-3 p-2.5 bg-rose-500/10 border border-rose-500/30 rounded-lg flex items-start gap-2"
          data-testid="mi-overdue-banner"
        >
          <Lightning size={13} weight="fill" className="text-rose-300 mt-0.5 flex-shrink-0" />
          <span className="text-rose-100 text-xs leading-snug">
            <span className="font-semibold">Your market has changed.</span>{' '}
            Refresh your strategy to keep every recommendation current.
          </span>
        </div>
      )}

      {/* Highest-ROI recommendation — hero row */}
      {summary.highest_roi && (
        <div
          className="mb-4 p-3 bg-white/5 border border-white/10 rounded-lg"
          data-testid="mi-highest-roi"
        >
          <div className="flex items-center gap-1.5 mb-1.5">
            <Target size={13} weight="fill" className="text-emerald-300" />
            <span className="text-emerald-300 text-xs font-semibold uppercase tracking-wide">Highest ROI</span>
          </div>
          <p className="text-white font-medium text-sm leading-snug mb-1">
            {summary.highest_roi.action}
          </p>
          {summary.highest_roi.why && (
            <p className="text-slate-300 text-xs leading-relaxed">
              {summary.highest_roi.why}
            </p>
          )}
          <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2 text-xs">
            {summary.highest_roi.expected_impact && (
              <span className="text-emerald-200/80">
                Impact: <span className="text-white">{summary.highest_roi.expected_impact}</span>
              </span>
            )}
            {summary.highest_roi.time_to_impact && (
              <span className="text-emerald-200/80">
                Time: <span className="text-white">{summary.highest_roi.time_to_impact}</span>
              </span>
            )}
          </div>
        </div>
      )}

      {/* Snapshot grid */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3">
        <div className="p-2 bg-white/5 rounded-lg">
          <div className="text-[10px] uppercase tracking-wide text-slate-400">Industry</div>
          <div className="text-white text-xs font-medium mt-0.5 truncate" title={summary.current_industry || '—'}>
            {summary.current_industry || '—'}
          </div>
        </div>
        <div className="p-2 bg-white/5 rounded-lg">
          <div className="text-[10px] uppercase tracking-wide text-slate-400">Primary Channel</div>
          <div className="text-white text-xs font-medium mt-0.5 truncate">
            {summary.primary_channel || '—'}
          </div>
        </div>
        <div className="p-2 bg-white/5 rounded-lg">
          <div className="text-[10px] uppercase tracking-wide text-slate-400">Confidence</div>
          <div className={`text-xs font-semibold mt-0.5 ${confidenceTone}`}>
            {confidence}%
            <button
              type="button"
              onClick={() => setShowReasons((v) => !v)}
              data-testid="mi-confidence-info-btn"
              className="ml-1 text-slate-400 hover:text-slate-200 align-middle"
            >
              <Info size={11} weight="bold" />
            </button>
          </div>
        </div>
      </div>

      {showReasons && Array.isArray(summary.confidence_reasons) && summary.confidence_reasons.length > 0 && (
        <div
          className="mb-3 p-2.5 bg-slate-800/60 border border-white/10 rounded-lg text-xs text-slate-300"
          data-testid="mi-confidence-reasons"
        >
          <div className="font-semibold text-slate-200 mb-1">Why this score:</div>
          <ul className="space-y-0.5 list-disc list-inside">
            {summary.confidence_reasons.map((r, i) => (
              <li key={`mi-reason-${i}`} className="marker:text-emerald-400">{r}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Current customer */}
      {summary.current_audience && (
        <div className="mb-3 p-2 bg-white/5 rounded-lg">
          <div className="text-[10px] uppercase tracking-wide text-slate-400">Current Customer</div>
          <div className="text-slate-200 text-xs mt-0.5 leading-relaxed">
            {summary.current_audience}
          </div>
        </div>
      )}

      {/* Top recommendations */}
      {Array.isArray(summary.top_recommendations) && summary.top_recommendations.length > 0 && (
        <div data-testid="mi-top-recommendations">
          <div className="text-xs font-semibold text-white mb-2">Top 30-day priorities</div>
          <ul className="space-y-1.5">
            {summary.top_recommendations.slice(0, 4).map((rec, i) => (
              <li
                key={`mi-rec-${i}`}
                className="flex items-start gap-2 text-xs text-slate-200"
                data-testid={`mi-recommendation-${i}`}
              >
                <span className="mt-0.5 w-4 h-4 flex items-center justify-center rounded-full bg-emerald-500/20 text-emerald-300 font-bold text-[10px] flex-shrink-0">
                  {i + 1}
                </span>
                <span className="leading-snug">{rec}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Marketing channels chips */}
      {Array.isArray(summary.best_marketing_channels) && summary.best_marketing_channels.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-1.5">
          <span className="text-[10px] uppercase tracking-wide text-slate-400 mr-1">Channels:</span>
          {summary.best_marketing_channels.map((c, i) => (
            <span
              key={`mi-channel-${i}`}
              className="px-2 py-0.5 bg-white/5 text-slate-200 text-[10px] rounded-full border border-white/10"
              data-testid={`mi-channel-${i}`}
            >
              {c}
            </span>
          ))}
        </div>
      )}

      {error && (
        <div className="mt-3 text-xs text-rose-300" data-testid="mi-error">
          {error}
        </div>
      )}
    </motion.div>
  );
};

export default MarketIntelligenceCard;
